import { postsService } from './posts';
import { eventService } from '../eventService';
import { normalizeTrackedPostMutationState } from '../../utils/postMutationState';
import {
    ENTITY_TIMELINE_ITEM_KIND,
    ENTITY_TIMELINE_PAGE_LIMIT,
    normalizeEntityObjectType,
    normalizeEntityTimelineItems,
    normalizeEntityTimelinePage,
} from '../../utils/entityTimeline';

const TIMELINE_KIND_POST = 'POST';
const TIMELINE_KIND_ENTITY = ENTITY_TIMELINE_ITEM_KIND;
const TIMELINE_KIND_SUGGESTED_USER = 'SUGGESTED_USER';
const POST_TIMELINE_PAGE_LIMIT = 25;
const EVENT_TIMELINE_OBJECT_TYPES = Object.freeze(['EVENT', 'SESSION']);
const EVENT_TIMELINE_OBJECT_TYPE_SET = new Set(EVENT_TIMELINE_OBJECT_TYPES);

function trackPromise(promise) {
    let state = 'pending';
    let value;
    let reason;
    const wrapped = Promise.resolve(promise).then(
        (result) => {
            state = 'fulfilled';
            value = result;
            return result;
        },
        (error) => {
            state = 'rejected';
            reason = error;
            throw error;
        },
    );
    return {
        promise: wrapped,
        getState: () => state,
        getValue: () => value,
        getReason: () => reason,
    };
}

function normalizePostTimelineItem(post) {
    return {
        ...post,
        timelineItemKind: TIMELINE_KIND_POST,
    };
}

function normalizePostTimelineItems(posts) {
    return Array.isArray(posts)
        ? posts.filter((post) => post && typeof post === 'object').map(normalizePostTimelineItem)
        : [];
}

function normalizePostTimelinePage(page, pageOptions = {}) {
    if (Array.isArray(page)) {
        return {
            items: normalizePostTimelineItems(page),
            hasMore: false,
            nextOffset: null,
        };
    }

    const items = normalizePostTimelineItems(page?.items);
    const pageLimit = typeof pageOptions.limit === 'number'
        ? Math.max(1, Math.floor(pageOptions.limit))
        : POST_TIMELINE_PAGE_LIMIT;
    const fallbackNextOffset = typeof pageOptions.offset === 'number'
        ? Math.max(0, Math.floor(pageOptions.offset)) + pageLimit
        : pageLimit;

    return {
        items,
        hasMore: page?.hasMore === true,
        nextOffset: typeof page?.nextOffset === 'number' ? page.nextOffset : (page?.hasMore === true ? fallbackNextOffset : null),
    };
}

function extractPostTimelineItems(items) {
    if (!Array.isArray(items)) {
        return [];
    }
    return normalizePostTimelineItems(
        items.filter((item) => item?.timelineItemKind === TIMELINE_KIND_POST),
    );
}

function extractEntityTimelineItems(items) {
    if (!Array.isArray(items)) {
        return [];
    }
    return filterEntityTimelineItemsByObjectTypes(
        items.filter((item) => item?.timelineItemKind === TIMELINE_KIND_ENTITY || normalizeEntityObjectType(item?.objectType)),
        EVENT_TIMELINE_OBJECT_TYPE_SET,
    );
}

function filterEntityTimelineItemsByObjectTypes(items, allowedObjectTypes) {
    const normalizedAllowedTypes = allowedObjectTypes instanceof Set
        ? allowedObjectTypes
        : new Set(Array.isArray(allowedObjectTypes) ? allowedObjectTypes : []);
    return normalizeEntityTimelineItems(items)
        .filter((item) => normalizedAllowedTypes.has(item.objectType));
}

function resolveSeedOptionValue(optionValue) {
    return typeof optionValue === 'function' ? optionValue() : optionValue;
}

function resolveSeedItems(seedItemsOption) {
    const resolvedSeedItems = resolveSeedOptionValue(seedItemsOption);
    return Array.isArray(resolvedSeedItems) ? resolvedSeedItems : [];
}

function resolveSeedPostMutationStateValue(seedPostMutationStateOption) {
    const resolvedSeedPostMutationState = resolveSeedOptionValue(seedPostMutationStateOption);
    return resolvedSeedPostMutationState && typeof resolvedSeedPostMutationState === 'object'
        ? resolvedSeedPostMutationState
        : {};
}

function resolveSeedState(options) {
    const seedItems = resolveSeedItems(options?.seedItems);
    return {
        seedItems,
        seedPostItems: extractPostTimelineItems(seedItems),
        seedEntityItems: extractEntityTimelineItems(seedItems),
        trackedSeedPostMutations: normalizeTrackedPostMutationState(
            resolveSeedPostMutationStateValue(options?.seedPostMutationState),
        ),
    };
}

function areEquivalentTimelineValues(left, right) {
    if (left === right) {
        return true;
    }

    const leftIsStructured = Boolean(left) && typeof left === 'object';
    const rightIsStructured = Boolean(right) && typeof right === 'object';
    if (!leftIsStructured || !rightIsStructured) {
        return false;
    }

    try {
        return JSON.stringify(left) === JSON.stringify(right);
    } catch (_) {
        return false;
    }
}

function mergeTrackedSeededPostFields(post, seededPost, trackedFields) {
    if (!trackedFields || trackedFields.size === 0) {
        return post;
    }

    let mergedPost = post;

    trackedFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(seededPost, field)) {
            return;
        }

        if (areEquivalentTimelineValues(post?.[field], seededPost?.[field])) {
            return;
        }

        if (mergedPost === post) {
            mergedPost = {
                ...post,
                timelineItemKind: TIMELINE_KIND_POST,
            };
        }
        mergedPost[field] = seededPost[field];
    });

    return mergedPost;
}

function mergeSeededPostTimelineItems(seedPostItems, fetchedPosts, options, trackedSeedPostMutations) {
    const normalizedSeedPosts = Array.isArray(seedPostItems)
        ? normalizePostTimelineItems(seedPostItems)
        : [];

    if (!Array.isArray(fetchedPosts)) {
        return normalizedSeedPosts;
    }

    const normalizedFetchedPosts = normalizePostTimelineItems(fetchedPosts);
    if (!options?.requirePostSource || normalizedSeedPosts.length === 0) {
        return normalizedFetchedPosts;
    }

    const seededByKey = new Map(
        normalizedSeedPosts.map((post) => [buildTimelineItemKey(post), post]),
    );

    return normalizedFetchedPosts.map((post) => {
        const itemKey = buildTimelineItemKey(post);
        const seededPost = seededByKey.get(itemKey);
        if (!seededPost) {
            return post;
        }

        const fetchedUpdatedAt = Date.parse(post?.updatedAt || post?.createdAt || '') || 0;
        const seededUpdatedAt = Date.parse(seededPost?.updatedAt || seededPost?.createdAt || '') || 0;
        if (seededUpdatedAt > fetchedUpdatedAt) {
            return {
                ...post,
                ...seededPost,
                timelineItemKind: TIMELINE_KIND_POST,
            };
        }

        return mergeTrackedSeededPostFields(
            post,
            seededPost,
            trackedSeedPostMutations.get(String(post?.id ?? '')),
        );
    });
}

function sortByCreatedAtDesc(items) {
    return [...items].sort((left, right) => {
        const leftTime = Date.parse(left?.createdAt || '') || 0;
        const rightTime = Date.parse(right?.createdAt || '') || 0;
        if (rightTime !== leftTime) {
            return rightTime - leftTime;
        }
        const leftKey = buildTimelineItemKey(left);
        const rightKey = buildTimelineItemKey(right);
        return rightKey.localeCompare(leftKey);
    });
}

function buildTimelineItemKey(item) {
    const kind = typeof item?.timelineItemKind === 'string'
        ? item.timelineItemKind
        : 'UNKNOWN';
    const id = item?.id == null ? '' : String(item.id);
    return `${kind}:${id}`;
}

function mergeNormalizedTimelineItems(existingItems, additionalItems) {
    const mergedByKey = new Map();
    for (const item of [...(Array.isArray(existingItems) ? existingItems : []), ...(Array.isArray(additionalItems) ? additionalItems : [])]) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        mergedByKey.set(buildTimelineItemKey(item), item);
    }
    return sortByCreatedAtDesc(Array.from(mergedByKey.values()));
}

function appendNewTimelineItems(existingItems, additionalItems, excludedKeys = new Set()) {
    const mergedByKey = new Map();
    for (const item of (Array.isArray(existingItems) ? existingItems : [])) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        mergedByKey.set(buildTimelineItemKey(item), item);
    }
    for (const item of (Array.isArray(additionalItems) ? additionalItems : [])) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const itemKey = buildTimelineItemKey(item);
        if (excludedKeys.has(itemKey) || mergedByKey.has(itemKey)) {
            continue;
        }
        mergedByKey.set(itemKey, item);
    }
    return sortByCreatedAtDesc(Array.from(mergedByKey.values()));
}

function findRemovedTimelineItemKeys(previousItems, latestItems) {
    const latestKeys = new Set(
        (Array.isArray(latestItems) ? latestItems : [])
            .filter((item) => item && typeof item === 'object')
            .map(buildTimelineItemKey),
    );
    return new Set(
        (Array.isArray(previousItems) ? previousItems : [])
            .filter((item) => item && typeof item === 'object')
            .map(buildTimelineItemKey)
            .filter((itemKey) => !latestKeys.has(itemKey)),
    );
}

function normalizeTimelinePageCount(value, fallback) {
    return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : fallback;
}

function attachFailedObjectTypes(items, failedObjectTypes) {
    const normalizedFailedTypes = Array.isArray(failedObjectTypes)
        ? failedObjectTypes.filter(Boolean)
        : [];
    if (normalizedFailedTypes.length === 0 || !Array.isArray(items)) {
        return items;
    }
    Object.defineProperty(items, 'failedObjectTypes', {
        configurable: true,
        enumerable: false,
        value: new Set(normalizedFailedTypes),
    });
    return items;
}

function getFailedObjectTypes(page) {
    return page?.failedObjectTypes instanceof Set ? page.failedObjectTypes : new Set();
}

function mergeEntityPageWithSeedFallback(page, seedEntityItems) {
    const failedObjectTypes = getFailedObjectTypes(page);
    const fetchedItems = normalizeEntityTimelineItems(page);
    if (failedObjectTypes.size === 0) {
        return fetchedItems;
    }
    const preservedSeedItems = normalizeEntityTimelineItems(seedEntityItems)
        .filter((item) => failedObjectTypes.has(item.objectType));
    return mergeNormalizedTimelineItems(fetchedItems, preservedSeedItems);
}

function createMergedEntityTimelinePageFetcher(sources) {
    const sourceStates = sources.map((source) => ({
        fetchPage: source.fetchPage,
        objectTypes: Array.isArray(source.objectTypes) ? source.objectTypes : [],
        items: [],
        nextOffset: 0,
        reachedEnd: false,
        error: null,
    }));

    const ensureSourceItems = async (state, requiredCount) => {
        while (state.items.length < requiredCount && !state.reachedEnd) {
            try {
                const page = normalizeEntityTimelinePage(await state.fetchPage({
                    limit: ENTITY_TIMELINE_PAGE_LIMIT,
                    offset: state.nextOffset,
                }));
                state.error = null;
                const normalizedPage = filterEntityTimelineItemsByObjectTypes(page, state.objectTypes);
                if (normalizedPage.length > 0) {
                    state.items = mergeNormalizedTimelineItems(state.items, normalizedPage);
                }
                state.nextOffset += page.length;
                if (page.length < ENTITY_TIMELINE_PAGE_LIMIT) {
                    state.reachedEnd = true;
                }
                if (page.length === 0) {
                    break;
                }
            } catch (error) {
                state.error = error;
                state.reachedEnd = true;
                break;
            }
        }
        return state;
    };

    return async (pageOptions = {}) => {
        const pageLimit = Math.min(
            ENTITY_TIMELINE_PAGE_LIMIT,
            Math.max(1, normalizeTimelinePageCount(pageOptions.limit, ENTITY_TIMELINE_PAGE_LIMIT)),
        );
        const pageOffset = normalizeTimelinePageCount(pageOptions.offset, 0);
        const requiredCount = pageOffset + pageLimit;

        await Promise.all(sourceStates.map((state) => ensureSourceItems(state, requiredCount)));

        const failedObjectTypes = sourceStates
            .filter((state) => state.error)
            .flatMap((state) => state.objectTypes);
        const allItems = sourceStates.reduce(
            (mergedItems, state) => mergeNormalizedTimelineItems(mergedItems, state.items),
            [],
        );

        if (allItems.length === 0 && sourceStates.every((state) => state.error)) {
            throw sourceStates.find((state) => state.error)?.error || new Error('Failed to load entity timeline');
        }

        return attachFailedObjectTypes(
            allItems.slice(pageOffset, pageOffset + pageLimit),
            failedObjectTypes,
        );
    };
}

function createFeedEntityTimelineFetcher() {
    return createMergedEntityTimelinePageFetcher([
        {
            fetchPage: (pageOptions) => eventService.getFeedTimeline(pageOptions),
            objectTypes: EVENT_TIMELINE_OBJECT_TYPES,
        },
    ]);
}

function createCurrentUserEntityTimelineFetcher() {
    return createMergedEntityTimelinePageFetcher([
        {
            fetchPage: (pageOptions) => eventService.getCurrentUserTimeline(pageOptions),
            objectTypes: EVENT_TIMELINE_OBJECT_TYPES,
        },
    ]);
}

function createUserEntityTimelineFetcher(userId) {
    return createMergedEntityTimelinePageFetcher([
        {
            fetchPage: (pageOptions) => eventService.getUserTimeline(userId, pageOptions),
            objectTypes: EVENT_TIMELINE_OBJECT_TYPES,
        },
    ]);
}

function createPublicUserEntityTimelineFetcher(userId) {
    return createMergedEntityTimelinePageFetcher([
        {
            fetchPage: (pageOptions) => eventService.getPublicUserTimeline(userId, pageOptions),
            objectTypes: EVENT_TIMELINE_OBJECT_TYPES,
        },
    ]);
}

function createEmptyEntityTimelineFetcher() {
    return async () => [];
}

function createEntityTimelineFetcher(options, factory) {
    return options?.includeEntityItems === false
        ? createEmptyEntityTimelineFetcher()
        : factory();
}

async function createTimelineLoad(fetchPostPage, fetchEntityPage, options = {}) {
    const hasSeedPostMutationState = Object.prototype.hasOwnProperty.call(options, 'seedPostMutationState');
    const loadPostPage = async (pageOptions = {}) => {
        const normalizedOptions = {
            limit: POST_TIMELINE_PAGE_LIMIT,
            offset: 0,
            ...pageOptions,
        };
        return normalizePostTimelinePage(await fetchPostPage(normalizedOptions), normalizedOptions);
    };
    const initialPostPageRequest = trackPromise(loadPostPage({
        limit: POST_TIMELINE_PAGE_LIMIT,
        offset: 0,
    }));
    const postRequest = trackPromise(initialPostPageRequest.promise.then((page) => page.items));
    const initialEntityPageRequest = trackPromise(
        fetchEntityPage({
            limit: ENTITY_TIMELINE_PAGE_LIMIT,
            offset: 0,
        }).then(normalizeEntityTimelinePage),
    );

    let posts = postRequest.getState() === 'fulfilled' ? postRequest.getValue() : null;
    let postError = postRequest.getState() === 'rejected' ? postRequest.getReason() : null;
    let initialEntityPage = initialEntityPageRequest.getState() === 'fulfilled'
        ? initialEntityPageRequest.getValue()
        : null;
    let entityError = initialEntityPageRequest.getState() === 'rejected'
        ? initialEntityPageRequest.getReason()
        : null;
    let initialPostPage = initialPostPageRequest.getState() === 'fulfilled'
        ? initialPostPageRequest.getValue()
        : null;
    let postHasMore = initialPostPage?.hasMore === true;
    let postNextOffset = typeof initialPostPage?.nextOffset === 'number'
        ? initialPostPage.nextOffset
        : POST_TIMELINE_PAGE_LIMIT;
    let entityHasMore = Array.isArray(initialEntityPage)
        && initialEntityPage.length >= ENTITY_TIMELINE_PAGE_LIMIT;
    let entityNextOffset = Array.isArray(initialEntityPage) ? initialEntityPage.length : ENTITY_TIMELINE_PAGE_LIMIT;

    const captureInitialPostPageMetadata = () => {
        if (initialPostPage == null && initialPostPageRequest.getState() === 'fulfilled') {
            initialPostPage = initialPostPageRequest.getValue();
        }
        if (initialPostPage != null) {
            postHasMore = initialPostPage.hasMore === true;
            postNextOffset = typeof initialPostPage.nextOffset === 'number'
                ? initialPostPage.nextOffset
                : POST_TIMELINE_PAGE_LIMIT;
        }
    };

    const captureInitialEntityPageMetadata = () => {
        if (Array.isArray(initialEntityPage)) {
            entityHasMore = initialEntityPage.length >= ENTITY_TIMELINE_PAGE_LIMIT;
            entityNextOffset = initialEntityPage.length;
        }
    };

    if (options.requirePostSource) {
        try {
            posts = await postRequest.promise;
            captureInitialPostPageMetadata();
        } catch (error) {
            throw error;
        }
    } else {
        while (posts == null && initialEntityPage == null) {
            if (postError && entityError) {
                throw postError || entityError || new Error('Failed to load timeline');
            }
            const pendingSources = [];
            if (posts == null && !postError) {
                pendingSources.push(
                    postRequest.promise.then(
                        (value) => ({ type: 'posts', value }),
                        (error) => ({ type: 'postError', error }),
                    ),
                );
            }
            if (initialEntityPage == null && !entityError) {
                pendingSources.push(
                    initialEntityPageRequest.promise.then(
                        (value) => ({ type: 'entity', value }),
                        (error) => ({ type: 'entityError', error }),
                    ),
                );
            }
            const settledSource = await Promise.race(pendingSources);
            if (settledSource.type === 'posts') {
                posts = settledSource.value;
                captureInitialPostPageMetadata();
            } else if (settledSource.type === 'entity') {
                initialEntityPage = settledSource.value;
                captureInitialEntityPageMetadata();
            } else if (settledSource.type === 'postError') {
                postError = settledSource.error;
            } else if (settledSource.type === 'entityError') {
                entityError = settledSource.error;
            }
        }
    }

    if (posts == null && postRequest.getState() === 'fulfilled') {
        posts = postRequest.getValue();
        captureInitialPostPageMetadata();
    }
    if (postError == null && postRequest.getState() === 'rejected') {
        postError = postRequest.getReason();
    }
    if (
        !options.requirePostSource
        && Array.isArray(posts)
        && normalizePostTimelineItems(posts).length === 0
        && initialEntityPage == null
        && !entityError
    ) {
        try {
            initialEntityPage = await initialEntityPageRequest.promise;
            captureInitialEntityPageMetadata();
        } catch (error) {
            entityError = error;
        }
    }
    if (initialEntityPage == null && initialEntityPageRequest.getState() === 'fulfilled') {
        initialEntityPage = initialEntityPageRequest.getValue();
        captureInitialEntityPageMetadata();
    }
    if (entityError == null && initialEntityPageRequest.getState() === 'rejected') {
        entityError = initialEntityPageRequest.getReason();
    }

    const hasAppliedPostItemsInitially = posts != null;
    const hasAppliedInitialEntityPageInitially = initialEntityPage != null;
    const initialSeedState = resolveSeedState(options);
    let nextSeedPostMutationState = hasSeedPostMutationState
        ? resolveSeedPostMutationStateValue(options.seedPostMutationState)
        : undefined;

    let postItems = mergeSeededPostTimelineItems(
        initialSeedState.seedPostItems,
        posts,
        options,
        initialSeedState.trackedSeedPostMutations,
    );
    let entityItems = Array.isArray(initialEntityPage)
        ? mergeEntityPageWithSeedFallback(initialEntityPage, initialSeedState.seedEntityItems)
        : initialSeedState.seedEntityItems;
    let currentItems = mergeNormalizedTimelineItems(postItems, entityItems);
    let hasAppliedPostItems = hasAppliedPostItemsInitially;
    let hasAppliedInitialEntityPage = hasAppliedInitialEntityPageInitially;

    if (hasAppliedPostItemsInitially && hasSeedPostMutationState) {
        nextSeedPostMutationState = {};
    }

    const syncCurrentItems = () => {
        currentItems = mergeNormalizedTimelineItems(postItems, entityItems);
        return currentItems;
    };

    const applyTimelineUpdate = (onUpdate, buildNextItems) => {
        if (typeof onUpdate !== 'function') {
            return null;
        }
        let appliedItems = null;
        const returnedItems = onUpdate((latestItems) => {
            appliedItems = buildNextItems(latestItems);
            return appliedItems;
        });
        if (Array.isArray(returnedItems)) {
            appliedItems = returnedItems;
        }
        if (Array.isArray(appliedItems)) {
            postItems = extractPostTimelineItems(appliedItems);
            entityItems = extractEntityTimelineItems(appliedItems);
            currentItems = mergeNormalizedTimelineItems(postItems, entityItems);
            updateTimelineLoadState();
            return currentItems;
        }
        return null;
    };

    const applyPostItems = (nextPostItems, onUpdate) => {
        const latestSeedState = resolveSeedState(options);
        postItems = mergeSeededPostTimelineItems(
            latestSeedState.seedPostItems,
            nextPostItems,
            options,
            latestSeedState.trackedSeedPostMutations,
        );
        if (hasSeedPostMutationState) {
            nextSeedPostMutationState = {};
            timelineLoad.nextSeedPostMutationState = nextSeedPostMutationState;
        }
        syncCurrentItems();
        if (typeof onUpdate === 'function') {
            applyTimelineUpdate(
                onUpdate,
                (latestItems) => mergeNormalizedTimelineItems(postItems, extractEntityTimelineItems(latestItems)),
            );
        }
        return currentItems;
    };

    const appendPostPageItems = (nextPageItems, onUpdate) => {
        const normalizedPageItems = normalizePostTimelineItems(nextPageItems);
        if (normalizedPageItems.length === 0) {
            return currentItems;
        }

        const appliedItems = applyTimelineUpdate(onUpdate, (latestItems) => {
            const latestPostItems = extractPostTimelineItems(latestItems);
            const removedPostKeys = findRemovedTimelineItemKeys(postItems, latestPostItems);
            const nextPostItems = appendNewTimelineItems(
                latestPostItems,
                normalizedPageItems,
                removedPostKeys,
            );
            return mergeNormalizedTimelineItems(nextPostItems, extractEntityTimelineItems(latestItems));
        });
        if (appliedItems) {
            return appliedItems;
        }

        postItems = appendNewTimelineItems(postItems, normalizedPageItems);
        return syncCurrentItems();
    };

    const applyEntityItems = (nextEntityItems, onUpdate) => {
        entityItems = Array.isArray(nextEntityItems) ? nextEntityItems : [];
        syncCurrentItems();
        if (typeof onUpdate === 'function') {
            applyTimelineUpdate(
                onUpdate,
                (latestItems) => mergeNormalizedTimelineItems(extractPostTimelineItems(latestItems), entityItems),
            );
        }
        return currentItems;
    };

    const hasMoreTimelineItems = () => (
        !hasAppliedPostItems
        || !hasAppliedInitialEntityPage
        || postHasMore
        || entityHasMore
    );

    const updateTimelineLoadState = () => {
        timelineLoad.items = currentItems;
        timelineLoad.hasMore = hasMoreTimelineItems();
        return currentItems;
    };

    const ensureInitialSourcesApplied = async (onUpdate) => {
        if (!hasAppliedPostItems) {
            try {
                const nextPosts = await postRequest.promise;
                hasAppliedPostItems = true;
                captureInitialPostPageMetadata();
                applyPostItems(normalizePostTimelineItems(nextPosts), onUpdate);
            } catch (_) {
                hasAppliedPostItems = true;
                postHasMore = false;
            }
        }

        if (!hasAppliedInitialEntityPage) {
            try {
                const nextInitialEntityPage = await initialEntityPageRequest.promise;
                hasAppliedInitialEntityPage = true;
                initialEntityPage = nextInitialEntityPage;
                captureInitialEntityPageMetadata();
                applyEntityItems(
                    mergeEntityPageWithSeedFallback(
                        nextInitialEntityPage,
                        resolveSeedState(options).seedEntityItems,
                    ),
                    onUpdate,
                );
            } catch (_) {
                hasAppliedInitialEntityPage = true;
                entityHasMore = false;
            }
        }

        return updateTimelineLoadState();
    };

    const loadNextPage = async (onUpdate) => {
        await ensureInitialSourcesApplied(onUpdate);

        const pageRequests = [];
        if (postHasMore && typeof postNextOffset === 'number') {
            pageRequests.push(
                loadPostPage({
                    limit: POST_TIMELINE_PAGE_LIMIT,
                    offset: postNextOffset,
                }).then(
                    (page) => ({ type: 'posts', page }),
                    (error) => ({ type: 'postError', error }),
                ),
            );
        }
        if (entityHasMore) {
            pageRequests.push(
                fetchEntityPage({
                    limit: ENTITY_TIMELINE_PAGE_LIMIT,
                    offset: entityNextOffset,
                }).then(normalizeEntityTimelinePage).then(
                    (page) => ({ type: 'entity', page }),
                    (error) => ({ type: 'entityError', error }),
                ),
            );
        }

        if (pageRequests.length === 0) {
            return updateTimelineLoadState();
        }

        const results = await Promise.all(pageRequests);
        let firstError = null;
        let appliedAnyPage = false;

        for (const result of results) {
            if (result.type === 'posts') {
                postHasMore = result.page.hasMore === true;
                postNextOffset = typeof result.page.nextOffset === 'number'
                    ? result.page.nextOffset
                    : null;
                if (result.page.items.length > 0) {
                    appliedAnyPage = true;
                    appendPostPageItems(result.page.items, onUpdate);
                }
                continue;
            }

            if (result.type === 'entity') {
                entityNextOffset += result.page.length;
                entityHasMore = result.page.length >= ENTITY_TIMELINE_PAGE_LIMIT;
                if (result.page.length > 0) {
                    appliedAnyPage = true;
                    applyEntityItems(
                        mergeNormalizedTimelineItems(entityItems, normalizeEntityTimelineItems(result.page)),
                        onUpdate,
                    );
                }
                continue;
            }

            if (result.type === 'postError') {
                postHasMore = false;
                firstError = firstError || result.error;
            }
            if (result.type === 'entityError') {
                entityHasMore = false;
                firstError = firstError || result.error;
            }
        }

        updateTimelineLoadState();
        if (!appliedAnyPage && firstError) {
            throw firstError;
        }
        return currentItems;
    };

    const loadRemaining = async (onUpdate) => {
        await ensureInitialSourcesApplied(onUpdate);

        let safetyCounter = 0;
        while ((postHasMore || entityHasMore) && safetyCounter < 200) {
            safetyCounter += 1;
            await loadNextPage(onUpdate);
        }

        if (safetyCounter >= 200 && (postHasMore || entityHasMore)) {
            throw new Error('Timeline pagination did not converge');
        }

        return currentItems;
    };

    const resolveAll = async () => {
        try {
            return await loadRemaining();
        } catch (_) {
            return currentItems;
        }
    };

    const timelineLoad = {
        items: currentItems,
        hasMore: hasMoreTimelineItems(),
        loadNextPage,
        loadRemaining,
        resolveAll,
        nextSeedPostMutationState,
    };

    return timelineLoad;
}

class TimelineService {
    async createFeedLoad(options = {}) {
        return createTimelineLoad(
            (pageOptions) => postsService.fetchFeedPage(pageOptions),
            createEntityTimelineFetcher(options, createFeedEntityTimelineFetcher),
            options,
        );
    }

    async fetchFeed(options = {}) {
        const load = await this.createFeedLoad(options);
        return load.resolveAll();
    }

    async createCurrentUserTimelineLoad(options = {}) {
        return createTimelineLoad(
            (pageOptions) => postsService.fetchUserPostsPage(pageOptions),
            createEntityTimelineFetcher(options, createCurrentUserEntityTimelineFetcher),
            options,
        );
    }

    async fetchCurrentUserTimeline(options = {}) {
        const load = await this.createCurrentUserTimelineLoad(options);
        return load.resolveAll();
    }

    async createUserTimelineLoad(userId, options = {}) {
        return createTimelineLoad(
            (pageOptions) => postsService.fetchUserPostsByIdPage(userId, pageOptions),
            createEntityTimelineFetcher(options, () => createUserEntityTimelineFetcher(userId)),
            options,
        );
    }

    async fetchUserTimeline(userId, options = {}) {
        const load = await this.createUserTimelineLoad(userId, options);
        return load.resolveAll();
    }

    async createPublicUserTimelineLoad(userId, options = {}) {
        const publicOptions = { ...options };
        return createTimelineLoad(
            (pageOptions) => postsService.fetchPublicUserPostsByIdPage(userId, pageOptions),
            createEntityTimelineFetcher(publicOptions, () => createPublicUserEntityTimelineFetcher(userId)),
            publicOptions,
        );
    }

    async fetchPublicUserTimeline(userId, options = {}) {
        const load = await this.createPublicUserTimelineLoad(userId, options);
        return load.resolveAll();
    }

    async createBandPostTimelineLoad(bandObjectId, options = {}) {
        const isPublicAudience = options?.audience === 'public';
        return createTimelineLoad(
            (pageOptions) => (
                isPublicAudience
                    ? postsService.fetchPublicBandPostsPage(bandObjectId, pageOptions)
                    : postsService.fetchBandPostsPage(bandObjectId, pageOptions)
            ),
            createEmptyEntityTimelineFetcher(),
            {
                ...options,
                includeEntityItems: false,
                requirePostSource: true,
            },
        );
    }

    async fetchBandPostTimeline(bandObjectId, options = {}) {
        const load = await this.createBandPostTimelineLoad(bandObjectId, options);
        return load.resolveAll();
    }
}

export const timelineService = new TimelineService();
export const TIMELINE_ITEM_KINDS = Object.freeze({
    POST: TIMELINE_KIND_POST,
    ENTITY: TIMELINE_KIND_ENTITY,
    SUGGESTED_USER: TIMELINE_KIND_SUGGESTED_USER,
});
